// File: frontend/src/components/RequirePage.js
//
// Route guard: renders the page only if the user may open it, otherwise shows
// the friendly AccessDenied screen. This is a UX gate — the backend still
// enforces every API call, so a user typing the URL directly sees AccessDenied
// AND any data request they attempt is independently rejected with 403.
//
// REGRESSION FIX: AccessDenied ("contact your administrator") is shown ONLY when
// a permissions load SUCCEEDED and confirms the page isn't allowed. A failed load
// (CORS / network / server error / expired session / stale-after-login) is NOT a
// denial — it gets a loading, session-expired, or retry state instead. Otherwise
// a real admin briefly (or until refresh) sees a scary "no access" page.

import React from "react";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { CloudOff as CloudOffIcon, LockClock as SessionIcon } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { usePermissions } from "../context/PermissionsContext";
import { authService } from "../services/api";
import AccessDenied from "./AccessDenied";

function CenterCard({ icon, title, body, action }) {
  return (
    <Box sx={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, px: 3, textAlign: "center" }}>
      {icon}
      <Typography variant="h6" fontWeight={800}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>{body}</Typography>
      {action}
    </Box>
  );
}

export default function RequirePage({ path, children }) {
  const { loading, error, canPage, reload } = usePermissions();
  const location = useLocation();
  const effectivePath = path || location.pathname;

  // 1. Affirmatively allowed → always render, even during a background refetch.
  //    (This is what prevents an access-denied flash right after logging in, when
  //    a fresh /me/permissions call is still in flight.)
  if (canPage(effectivePath)) return children;

  // 2. Not (yet) allowed but a load is in flight → wait; NEVER deny mid-load.
  if (loading) {
    return (
      <Box sx={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  // 3. The load FAILED — this is not a permission decision. Distinguish the cause
  //    so CORS/network/expired-session is never shown as "contact your administrator".
  if (error === "auth" || !authService.getCurrentUser()) {
    // 401/expired — the API interceptor also hard-redirects to /login; show a
    // graceful message meanwhile.
    return (
      <CenterCard
        icon={<SessionIcon sx={{ fontSize: 44, color: "warning.main" }} />}
        title="Your session expired"
        body="Please sign in again to continue."
        action={<Button variant="contained" onClick={() => { window.location.assign("/login"); }}>Go to login</Button>}
      />
    );
  }
  if (error) {
    // Network / CORS / server error — retry, do NOT deny.
    return (
      <CenterCard
        icon={<CloudOffIcon sx={{ fontSize: 44, color: "text.secondary" }} />}
        title="Couldn't verify your access"
        body="We couldn't reach the server to load your permissions. This is a connection issue, not a permission problem. Please try again."
        action={<Button variant="contained" onClick={() => reload()}>Retry</Button>}
      />
    );
  }

  // 4. A definitive, successful permissions load that does NOT allow this page →
  //    the only real access denial.
  console.warn(`[permissions] access denied for path="${effectivePath}" (confirmed load, page not in allowed set)`);
  return <AccessDenied />;
}
